/**
 * The topbar's breadcrumb, published by whichever page is on screen.
 *
 * `Header` is mounted once in `MainLayout`, above `children`, so a page cannot
 * render into it directly — which is how the builder topic ended up growing a
 * second bar of its own. A page calls `useBreadcrumb([...])` and the one
 * existing topbar renders the trail; pages that publish nothing leave the slot
 * empty.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface Crumb {
  label: string;
  /**
   * Where the crumb goes. Omit on the last crumb — it is the current page.
   * Navigation runs through `setHandleRedirect`, not a `<Link>`, so pages that
   * hold unsaved work (the answer editor) still get to prompt first; `projectId`
   * and `hubId` are what that action needs to know which guard applies.
   */
  href?: string;
  projectId?: number | null;
  hubId?: number;
}

interface BreadcrumbValue {
  trail: Crumb[];
  setTrail: (trail: Crumb[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbValue>({
  trail: [],
  setTrail: () => {},
});

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [trail, setTrail] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ trail, setTrail }), [trail]);
  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

/** Read the current trail. For the topbar. */
export const useBreadcrumbTrail = (): Crumb[] =>
  useContext(BreadcrumbContext).trail;

/**
 * Publish a trail for as long as this component is mounted, and clear it on the
 * way out so the next route doesn't inherit it.
 *
 * The trail is compared by value rather than by identity: call sites build the
 * array inline from props, so an identity check would re-set state on every
 * render and loop.
 */
export function useBreadcrumb(trail: Crumb[]): void {
  const { setTrail } = useContext(BreadcrumbContext);
  const serialized = JSON.stringify(trail);

  const publish = useCallback(() => {
    setTrail(JSON.parse(serialized) as Crumb[]);
  }, [serialized, setTrail]);

  useEffect(() => {
    publish();
    return () => setTrail([]);
  }, [publish, setTrail]);
}
